import { useEffect, useMemo, useState } from "react";
import { X, Download, Share, SquarePlus, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { API, fileUrl } from "../lib/api";
import {
  mergePwa, isStandalone, isIos, applyManifest, registerStoreSw,
  onInstallState, canPromptInstall, wasInstalled, promptInstall,
  isDismissed, dismissBanner, splashSeenKey,
} from "../lib/pwa";

export function SplashArt({ animation = "spinner", color = "#111111" }) {
  if (animation === "dots") {
    return (
      <div className="flex gap-1.5 mt-6">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-2.5 w-2.5 rounded-full animate-bounce" style={{ backgroundColor: color, animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>
    );
  }
  if (animation === "pulse") {
    return <span className="mt-6 h-8 w-8 rounded-full animate-ping" style={{ backgroundColor: color, opacity: 0.5 }} />;
  }
  return <span className="mt-6 h-9 w-9 rounded-full border-4 animate-spin" style={{ borderColor: `${color}33`, borderTopColor: color }} />;
}

export function AppIconTile({ pwa, slug, size = "h-12 w-12", text = "text-lg" }) {
  const src = pwa.icon ? fileUrl(pwa.icon) : `${API}/public/store/${slug}/pwa-icon/192.png`;
  if (pwa.icon) return <img src={src} alt="" className={`${size} rounded-2xl object-cover shadow-md`} />;
  return (
    <div className={`${size} rounded-2xl shadow-md flex items-center justify-center font-extrabold text-white ${text}`} style={{ backgroundColor: pwa.theme_color || "#111111" }}>
      {(pwa.app_name || "S")[0].toUpperCase()}
    </div>
  );
}

export default function PwaRuntime({ slug, store, pathname = "" }) {
  const pwa = useMemo(() => mergePwa(store), [store]);
  const isCheckout = pathname.includes("/checkout") || pathname.includes("/payment");
  const enabled = !!pwa.enabled && !!slug;
  const standalone = isStandalone();
  const [showSplash, setShowSplash] = useState(false);
  const [installReady, setInstallReady] = useState(canPromptInstall());
  const [gone, setGone] = useState(wasInstalled());
  const [dismissed, setDismissed] = useState(() => isDismissed(slug));
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => onInstallState(() => { setInstallReady(canPromptInstall()); setGone(wasInstalled()); }), []);

  useEffect(() => {
    if (!enabled) return;
    applyManifest(slug, pwa);
    registerStoreSw(slug);
  }, [enabled, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || !standalone) return;
    try { if (sessionStorage.getItem(splashSeenKey(slug))) return; } catch { /* noop */ }
    setShowSplash(true);
    const t = setTimeout(() => {
      setShowSplash(false);
      try { sessionStorage.setItem(splashSeenKey(slug), "1"); } catch { /* noop */ }
    }, Math.min(Math.max(Number(pwa.splash_duration_ms) || 0, 300), 5000));
    return () => clearTimeout(t);
  }, [enabled, standalone, slug, pwa.splash_duration_ms]);

  if (!enabled) return null;

  const doInstall = async () => {
    if (isIos() && !installReady) { setShowIosGuide(true); return; }
    const ok = await promptInstall();
    if (ok) { toast.success("App installed"); setDismissed(true); }
  };
  const onDismiss = () => { dismissBanner(slug); setDismissed(true); };
  const closeIosGuide = () => { setShowIosGuide(false); dismissBanner(slug); setDismissed(true); };

  const splashBg = pwa.splash_bg || pwa.background_color || "#ffffff";
  const logo = pwa.splash_logo || pwa.icon;
  const installable = installReady || isIos();
  const showBanner = !isCheckout && !standalone && !gone && !dismissed && pwa.promo_banner && installable;
  const showFab = !isCheckout && !standalone && !gone && pwa.install_button && installable && !showBanner;

  return (
    <>
      {showSplash && (
        <div data-testid="pwa-splash" className="fixed inset-0 z-[9999] flex flex-col items-center justify-center transition-opacity" style={{ backgroundColor: splashBg }}>
          {logo ? (
            <img src={fileUrl(logo)} alt="" className="h-20 w-20 rounded-3xl object-cover shadow-xl" />
          ) : (
            <AppIconTile pwa={pwa} slug={slug} size="h-20 w-20" text="text-3xl" />
          )}
          <SplashArt animation={pwa.splash_animation} color={pwa.theme_color} />
          {pwa.splash_text && <p className="mt-4 text-sm font-bold" style={{ color: pwa.theme_color }}>{pwa.splash_text}</p>}
          <p className="absolute bottom-10 text-xs font-extrabold tracking-widest uppercase" style={{ color: pwa.theme_color, opacity: 0.55 }}>{pwa.app_name}</p>
        </div>
      )}

      {showBanner && (
        <div data-testid="pwa-install-banner" className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:w-96 z-[9990] bg-white rounded-2xl shadow-2xl border border-black/10 p-4">
          <div className="flex items-center gap-3">
            <AppIconTile pwa={pwa} slug={slug} />
            <div className="flex-1 min-w-0">
              <p className="font-extrabold text-sm text-zinc-900 truncate">{pwa.app_name}</p>
              <p className="text-xs text-zinc-500">Get our app for a faster experience.</p>
            </div>
            <button data-testid="pwa-install-dismiss" onClick={onDismiss} aria-label="Dismiss"
              className="h-8 w-8 shrink-0 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-400 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
          <button data-testid="pwa-install-button" onClick={doInstall}
            className="mt-3 w-full h-11 rounded-full text-white text-sm font-extrabold active:scale-95 transition-transform flex items-center justify-center gap-2"
            style={{ backgroundColor: pwa.theme_color || "#111111" }}>
            <Download className="h-4 w-4" /> Install App
          </button>
        </div>
      )}

      {showFab && (
        <button data-testid="pwa-fab-install" onClick={doInstall}
          className="fixed bottom-5 right-5 z-[9990] h-12 pl-4 pr-5 rounded-full text-white text-sm font-extrabold shadow-xl flex items-center gap-2 active:scale-95 transition-transform"
          style={{ backgroundColor: pwa.theme_color || "#111111" }}>
          <Download className="h-4 w-4" /> Install App
        </button>
      )}

      {showIosGuide && (
        <div data-testid="ios-install-guide" className="fixed inset-0 z-[9995] flex items-end sm:items-center justify-center sm:p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.55)" }} onClick={closeIosGuide}>
          <div className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-1">
              <AppIconTile pwa={pwa} slug={slug} />
              <div className="flex-1 min-w-0">
                <p className="font-extrabold text-zinc-900 truncate">Install {pwa.app_name}</p>
                <p className="text-xs text-zinc-500">Add this app to your iPhone in 3 taps</p>
              </div>
              <button data-testid="ios-guide-close" onClick={closeIosGuide} aria-label="Close"
                className="h-8 w-8 shrink-0 rounded-full hover:bg-zinc-100 flex items-center justify-center text-zinc-400 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {[
                { icon: <Share className="h-5 w-5" style={{ color: pwa.theme_color }} />, title: "Tap the Share button", hint: "The square-with-arrow icon in Safari's bottom bar" },
                { icon: <SquarePlus className="h-5 w-5" style={{ color: pwa.theme_color }} />, title: 'Tap "Add to Home Screen"', hint: "Scroll down in the share sheet to find it" },
                { icon: <BadgeCheck className="h-5 w-5" style={{ color: pwa.theme_color }} />, title: 'Tap "Add"', hint: "Top-right corner — the app icon appears on your home screen" },
              ].map((s, i) => (
                <div key={i} data-testid={`ios-guide-step-${i + 1}`} className="flex items-center gap-3 rounded-2xl border border-zinc-200 p-3.5">
                  <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${pwa.theme_color}14` }}>{s.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-zinc-900">{i + 1}. {s.title}</p>
                    <p className="text-xs text-zinc-500">{s.hint}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-zinc-400 text-center">Using another browser? Open this site in Safari to install.</p>
            <button data-testid="ios-guide-done" onClick={closeIosGuide}
              className="mt-4 w-full h-11 rounded-full text-white text-sm font-extrabold active:scale-95 transition-transform"
              style={{ backgroundColor: pwa.theme_color || "#111111" }}>
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
